import { captureMessage } from './sentry';
import { initMixpanelAnalytics, setMixpanelConsent } from '../analytics/analytics';

// Google Analytics 4 配置
export const GA_TRACKING_ID = import.meta.env.VITE_GA_TRACKING_ID;

/** 與 UserPreferences.data_sharing 同步；為 false 時不送 GA / Mixpanel 事件 */
let analyticsDataSharingApproved = true;

export function setAnalyticsDataSharingApproved(approved: boolean): void {
  analyticsDataSharingApproved = approved;
  setMixpanelConsent(approved);
  if (approved) {
    initMixpanelAnalytics();
  }
}

export function canSendAnalytics(): boolean {
  return (
    Boolean(GA_TRACKING_ID) &&
    typeof window !== 'undefined' &&
    typeof window.gtag === 'function' &&
    analyticsDataSharingApproved
  );
}

// 初始化 Google Analytics
export const initGA = () => {
  if (!GA_TRACKING_ID) {
    console.warn('Google Analytics tracking ID not found, analytics disabled');
    return;
  }

  // 載入 Google Analytics 腳本
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_TRACKING_ID}`;
  document.head.appendChild(script);

  // 初始化 gtag
  window.dataLayer = window.dataLayer || [];
  function gtag(...args: any[]) {
    window.dataLayer.push(args);
  }
  window.gtag = gtag;

  gtag('js', new Date());
  gtag('config', GA_TRACKING_ID, {
    // 自定義配置
    custom_map: {
      dimension1: 'user_type',
      dimension2: 'user_id',
      dimension3: 'company_id',
    },
    // 隱私設定
    anonymize_ip: true,
    allow_ad_features: false,
  });

  console.log('✅ Google Analytics initialized');

  // 發送初始化事件到 Sentry（用於監控）
  captureMessage('Google Analytics initialized', 'info', {
    trackingId: GA_TRACKING_ID,
    timestamp: new Date().toISOString(),
  });
};

// 自定義事件追蹤
export const trackEvent = (
  action: string,
  category: string,
  label?: string,
  value?: number,
  customParameters?: Record<string, any>
) => {
  if (!window.gtag || !GA_TRACKING_ID) {
    console.warn('Google Analytics not initialized');
    return;
  }

  const eventParams: any = {
    event_category: category,
    event_label: label,
    value: value,
    ...customParameters,
  };

  window.gtag('event', action, eventParams);
};

// 頁面瀏覽追蹤
export const trackPageView = (pagePath: string, pageTitle?: string) => {
  if (!canSendAnalytics()) {
    return;
  }

  window.gtag('config', GA_TRACKING_ID, {
    page_path: pagePath,
    page_title: pageTitle,
  });
};

// 用戶屬性設置
export const setUserProperties = (properties: Record<string, any>) => {
  if (!window.gtag || !GA_TRACKING_ID) {
    return;
  }

  window.gtag('config', GA_TRACKING_ID, {
    custom_map: Object.keys(properties).reduce((acc, key, index) => {
      acc[`dimension${index + 1}`] = key;
      return acc;
    }, {} as Record<string, string>),
  });

  // 發送用戶屬性事件
  window.gtag('event', 'user_properties_set', {
    ...properties,
  });
};

// 電子商務事件（適用於服務預約）
export const trackServiceRequest = (serviceData: {
  serviceId: string;
  serviceName: string;
  price: number;
  category: string;
}) => {
  if (!canSendAnalytics()) {
    return;
  }

  window.gtag('event', 'service_request', {
    currency: 'TWD',
    value: serviceData.price,
    items: [{
      item_id: serviceData.serviceId,
      item_name: serviceData.serviceName,
      category: serviceData.category,
      price: serviceData.price,
      quantity: 1,
    }],
  });
};

// 支付事件
export const trackPayment = (paymentData: {
  transactionId: string;
  value: number;
  currency: string;
  paymentMethod: string;
  items?: Array<{
    item_id: string;
    item_name: string;
    category: string;
    price: number;
    quantity: number;
  }>;
}) => {
  if (!canSendAnalytics()) {
    return;
  }

  window.gtag('event', 'purchase', {
    transaction_id: paymentData.transactionId,
    currency: paymentData.currency,
    value: paymentData.value,
    payment_type: paymentData.paymentMethod,
    items: paymentData.items,
  });
};

// 用戶註冊/登入事件
export const trackAuth = (action: 'login' | 'signup' | 'logout', method?: string) => {
  if (!window.gtag || !GA_TRACKING_ID) {
    return;
  }

  window.gtag('event', action, {
    method: method || 'email',
  });
};

// 錯誤追蹤（補充 Sentry）
export const trackError = (error: Error, context?: Record<string, any>) => {
  if (!window.gtag || !GA_TRACKING_ID) {
    return;
  }

  window.gtag('event', 'exception', {
    description: error.message,
    fatal: false,
    ...context,
  });
};

// 性能追蹤
export const trackPerformance = (metric: string, value: number, context?: Record<string, any>) => {
  if (!canSendAnalytics()) {
    return;
  }

  window.gtag('event', 'performance', {
    metric,
    value,
    ...context,
  });
};

// 聲明全局 gtag 類型
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}
